import BaseApiClient from '../BaseApiClient';
import { filenameFromContentDisposition } from '../savings-products/response-helpers';
import type {
  ListDataParams,
  ListDataResponse,
  ListExportParams
} from '../types';
import type {
  SavingsCashAccountSummary,
  SavingsCustomerSummary,
  SavingsDepositSummary,
  SavingsInstruction,
  SavingsInstructionType,
  SavingsApprovalDecision,
  SavingsInstructionUpdatePayload,
  SavingsNoticeRequestPayload,
  SavingsProcessingPayload,
  SavingsProductPickerOption
} from '@/interfaces/SavingsInstruction.interface';
import type { SavingsProductListItem } from '@/interfaces/SavingsProduct.interface';

const API_VERSION = { Version: '2026-01-20' };
const LOOKUP_PAGE_SIZE = 25;
/** Lookups a customer has only a handful of — fetched as a single page. */
const SINGLE_PAGE = { page: 1, perPage: 100 };

interface PaginatedEnvelope<T> {
  data: T[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page?: number;
  };
}

const toListResponse = <T>(
  envelope: PaginatedEnvelope<T>
): ListDataResponse<T> => ({
  items: envelope.data,
  pagination: {
    page: envelope.meta.current_page,
    perPage: envelope.meta.per_page,
    total: envelope.meta.total,
    lastPage: envelope.meta.last_page
  }
});

export class ApiSavingsInstructions extends BaseApiClient {
  private async getPage<T>(
    url: string,
    listData: ListDataParams
  ): Promise<ListDataResponse<T>> {
    const res = await this.client.getList(url, listData, {
      headers: API_VERSION
    });
    return toListResponse<T>(res.data);
  }

  list(
    listData: ListDataParams
  ): Promise<ListDataResponse<SavingsInstruction>> {
    return this.getPage('/savings-instructions', listData);
  }

  /** By permanent ID, or by legacy instruction ID for old links (FLT-5638). */
  async get(id: number | string): Promise<SavingsInstruction> {
    const res = await this.client.get(
      `/savings-instructions/${encodeURIComponent(String(id))}`,
      {
        headers: API_VERSION
      }
    );
    return res.data.data;
  }

  async getPdf(id: number): Promise<{ blob: Blob; filename: string }> {
    const response = await this.client.get(`/savings-instructions/${id}/pdf`, {
      responseType: 'blob',
      headers: { ...API_VERSION, Accept: 'application/pdf' }
    });
    return {
      blob: new Blob([response.data], { type: 'application/pdf' }),
      filename: filenameFromContentDisposition(
        response.headers['content-disposition'],
        `savings-instruction-${id}.pdf`
      )
    };
  }

  async update(
    id: number,
    payload: SavingsInstructionUpdatePayload
  ): Promise<SavingsInstruction> {
    const res = await this.client.patch(
      `/savings-instructions/${id}`,
      payload,
      { headers: API_VERSION }
    );
    return res.data.data;
  }

  /** Approves or rejects the instruction's group on the customer's behalf (FLT-5634). */
  async decideApproval(
    id: number,
    decision: SavingsApprovalDecision,
    reason: string
  ): Promise<SavingsInstruction> {
    const res = await this.client.post(
      `/savings-instructions/${id}/approval`,
      { decision, reason },
      { headers: API_VERSION }
    );
    return res.data.data;
  }

  /** Processes an instruction (FLT-5633). */
  async process(
    id: number,
    payload: SavingsProcessingPayload
  ): Promise<SavingsInstruction> {
    const res = await this.client.post(
      `/savings-instructions/${id}/process`,
      payload,
      { headers: API_VERSION }
    );
    return res.data.data;
  }

  /** Raises an edit or cancel request on a deposit notice (FLT-5644). */
  async raiseNoticeRequest(
    id: number,
    payload: SavingsNoticeRequestPayload
  ): Promise<SavingsInstruction> {
    const res = await this.client.post(
      `/savings-instructions/${id}/notice-requests`,
      payload,
      { headers: API_VERSION }
    );
    return res.data.data;
  }

  async export(
    listData: ListExportParams,
    searchTerm?: string
  ): Promise<{ blob: Blob; filename: string }> {
    const trimmedSearch = searchTerm?.trim();
    const filters: Record<string, unknown> = {
      ...listData.filters,
      ...(trimmedSearch ? { search: trimmedSearch } : {})
    };
    const response = await this.client.getList(
      '/savings-instructions/export',
      { filters, sort: listData.sort },
      {
        responseType: 'blob',
        headers: { ...API_VERSION, Accept: 'text/csv' }
      }
    );
    const direction =
      typeof filters.direction === 'string' ? filters.direction : 'all';

    return {
      blob: new Blob([response.data], { type: 'text/csv;charset=utf-8' }),
      filename: filenameFromContentDisposition(
        response.headers['content-disposition'],
        `savings-${direction}-instructions.csv`
      )
    };
  }

  listCustomers(
    searchTerm?: string
  ): Promise<ListDataResponse<SavingsCustomerSummary>> {
    const trimmed = searchTerm?.trim();
    return this.getPage('/savings-customers', {
      page: 1,
      perPage: LOOKUP_PAGE_SIZE,
      ...(trimmed ? { filters: { search: trimmed } } : {})
    });
  }

  async findCustomer(id: number): Promise<SavingsCustomerSummary | undefined> {
    const page = await this.getPage<SavingsCustomerSummary>(
      '/savings-customers',
      { page: 1, perPage: 1, filters: { id } }
    );
    return page.items[0];
  }

  listInstructionTypes(): Promise<ListDataResponse<SavingsInstructionType>> {
    return this.getPage('/savings-instruction-types', SINGLE_PAGE);
  }

  listCustomerCashAccounts(
    customerId: number
  ): Promise<ListDataResponse<SavingsCashAccountSummary>> {
    return this.getPage(
      `/savings-customers/${customerId}/cash-accounts`,
      SINGLE_PAGE
    );
  }

  listCustomerDeposits(
    customerId: number
  ): Promise<ListDataResponse<SavingsDepositSummary>> {
    return this.getPage(
      `/savings-customers/${customerId}/deposits`,
      SINGLE_PAGE
    );
  }

  async listProducts(
    searchTerm?: string
  ): Promise<ListDataResponse<SavingsProductPickerOption>> {
    const trimmed = searchTerm?.trim();
    const page = await this.getPage<SavingsProductListItem>(
      '/savings-products',
      {
        page: 1,
        perPage: LOOKUP_PAGE_SIZE,
        ...(trimmed ? { filters: { search: trimmed } } : {})
      }
    );
    return {
      ...page,
      items: page.items.map(({ id, name, currency }) => ({
        id,
        name,
        currency
      }))
    };
  }
}

export * from './query-keys';
export * from './use-savings-instructions-queries';
export * from './use-savings-instructions-mutations';
